import React, { useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { FaNetworkWired, FaChartLine, FaHeartbeat, FaClock } from 'react-icons/fa';
import { StationStats, AvailabilityTrend, KavachSwitch } from '../utils/kavachTypes';
import { KAVACH_COLORS, getSwitchHealth } from '../utils/kavachConstants';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement);

interface KavachChartsProps {
  stationStats: StationStats[];
  availabilityTrend: AvailabilityTrend[];
  switches: KavachSwitch[];
}

const KavachCharts: React.FC<KavachChartsProps> = ({ stationStats, availabilityTrend, switches }) => {
  // Network Status by Station (Bar Chart)
  const barChartData = useMemo(() => ({
    labels: stationStats.map((s) => s.station),
    datasets: [
      {
        label: 'Ports Up',
        data: stationStats.map((s) => s.portsUp),
        backgroundColor: KAVACH_COLORS.statusUp,
        borderRadius: 4,
      },
      {
        label: 'Ports Down',
        data: stationStats.map((s) => s.portsDown),
        backgroundColor: KAVACH_COLORS.statusDown,
        borderRadius: 4,
      },
    ],
  }), [stationStats]);

  const barChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          padding: 8,
          font: { size: 11 }
        }
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { font: { size: 10 } }
      },
    },
  }), []);

  // Port Availability Trend (Line Chart)
  const lineChartData = useMemo(() => ({
    labels: availabilityTrend.map((t) => t.time),
    datasets: [
      {
        label: 'Availability %',
        data: availabilityTrend.map((t) => t.availability),
        borderColor: KAVACH_COLORS.primary,
        backgroundColor: 'rgba(0, 86, 179, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: KAVACH_COLORS.primary,
      },
    ],
  }), [availabilityTrend]);

  const lineChartOptions = useMemo(() => {
    // Calculate dynamic min based on data
    const minAvailability = Math.min(...availabilityTrend.map((t) => t.availability));
    const dynamicMin = Math.max(0, Math.floor(minAvailability / 10) * 10 - 10);

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            boxWidth: 12,
            padding: 8,
            font: { size: 11 }
          }
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 } }
        },
        y: {
          beginAtZero: false,
          min: dynamicMin,
          max: 100,
          grid: { color: 'rgba(0, 0, 0, 0.05)' },
          ticks: {
            font: { size: 10 },
            callback: (value: number | string) => `${value}%`,
          },
        },
      },
    };
  }, [availabilityTrend]);

  // Switch Health Overview (Doughnut Chart)
  const healthCounts = useMemo(() => {
    const counts = { healthy: 0, partial: 0, critical: 0 };
    switches.forEach((sw) => {
      const health = getSwitchHealth(sw);
      counts[health]++;
    });
    return counts;
  }, [switches]);

  const doughnutChartData = useMemo(() => ({
    labels: ['Healthy', 'Partial', 'Critical'],
    datasets: [
      {
        data: [healthCounts.healthy, healthCounts.partial, healthCounts.critical],
        backgroundColor: [KAVACH_COLORS.statusUp, KAVACH_COLORS.statusPartial, KAVACH_COLORS.statusDown],
        borderWidth: 0,
      },
    ],
  }), [healthCounts]);

  const doughnutChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          boxWidth: 12,
          padding: 8,
          font: { size: 11 }
        }
      },
    },
    cutout: '60%',
  }), []);

  const currentTime = new Date().toLocaleTimeString();

  return (
    <>
      {/* Network Status by Station */}
      <div className="chart-card">
        <div className="chart-title">
          <h3>
            <FaNetworkWired />
            Network Status by Station
          </h3>
          <div className="refresh-indicator">
            <FaClock />
            <span>Updated: <span className="last-updated-time">{currentTime}</span></span>
          </div>
        </div>
        <div className="chart-container">
          <Bar data={barChartData} options={barChartOptions} />
        </div>
      </div>

      {/* Port Availability Trend */}
      <div className="chart-card">
        <div className="chart-title">
          <h3>
            <FaChartLine />
            Port Availability Trend
          </h3>
          <div className="refresh-indicator">
            <FaClock />
            <span>Updated: <span className="last-updated-time">{currentTime}</span></span>
          </div>
        </div>
        <div className="chart-container">
          <Line data={lineChartData} options={lineChartOptions} />
        </div>
      </div>

      {/* Switch Health Overview */}
      <div className="chart-card">
        <div className="chart-title">
          <h3>
            <FaHeartbeat />
            Switch Health Overview
          </h3>
          <div className="refresh-indicator">
            <FaClock />
            <span>Updated: <span className="last-updated-time">{currentTime}</span></span>
          </div>
        </div>
        <div className="chart-container">
          <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
        </div>
      </div>
    </>
  );
};

export default KavachCharts;
